import { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import {
  BookOpen,
  Copy,
  Check,
  Loader2,
  AlertTriangle,
  Zap,
  Activity,
  Code2,
  Lock,
  ArrowUpRight,
  Terminal,
} from "lucide-react";
import { Link } from "react-router-dom";
import ApiKeysCard from "@/components/ApiKeysCard";
import WebhooksCard from "@/components/WebhooksCard";

/**
 * Team-tier API Docs page.
 *
 * Sections:
 *   1. Quickstart (cURL, Python, Node)
 *   2. Per-key usage analytics (totals, 14-day sparkline, recent calls, rate window)
 *   3. Endpoint reference (rendered from /api/keys/docs/catalog)
 *   4. Inline ApiKeysCard for create/revoke without leaving the page
 */
export default function ApiDocs() {
  const { user } = useAuth();
  const [catalog, setCatalog] = useState(null);
  const [keys, setKeys] = useState([]);
  const [selectedKeyId, setSelectedKeyId] = useState(null);
  const [usage, setUsage] = useState(null);
  const [usageLoading, setUsageLoading] = useState(false);
  const [gated, setGated] = useState(false);
  const [loading, setLoading] = useState(true);

  const baseUrl = useMemo(() => {
    if (typeof window === "undefined") return "https://api.ispeedtoclose.com";
    return window.location.origin;
  }, []);

  const refreshKeys = useCallback(async () => {
    try {
      const r = await api.get("/keys");
      setKeys(r.data || []);
      if (!selectedKeyId && (r.data || []).length > 0) {
        setSelectedKeyId(r.data[0].id);
      }
    } catch (err) {
      if (err?.response?.status === 403) setGated(true);
    }
  }, [selectedKeyId]);

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        const r = await api.get("/keys/docs/catalog");
        if (alive) setCatalog(r.data);
      } catch (err) {
        if (alive && err?.response?.status === 403) setGated(true);
      }
      await refreshKeys();
      if (alive) setLoading(false);
    })();
    return () => { alive = false; };
  }, [refreshKeys]);

  const loadUsage = useCallback(async (id) => {
    if (!id) return;
    setUsageLoading(true);
    try {
      const r = await api.get(`/keys/${id}/usage`);
      setUsage(r.data);
    } catch {
      setUsage(null);
    } finally {
      setUsageLoading(false);
    }
  }, []);

  useEffect(() => {
    if (selectedKeyId) loadUsage(selectedKeyId);
    else setUsage(null);
  }, [selectedKeyId, loadUsage]);

  if (loading) {
    return (
      <div className="min-h-[40vh] flex items-center justify-center text-[var(--ink-3)]">
        <Loader2 className="animate-spin mr-2" size={16} /> Loading API docs…
      </div>
    );
  }

  if (gated || !catalog) {
    return <GatedView />;
  }

  const exampleKey = "pat_xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx";
  const meEndpoint = `${baseUrl}/api/auth/me`;

  return (
    <div className="p-6 max-w-6xl mx-auto" data-testid="api-docs-page">
      <header className="mb-6">
        <div className="label-eyebrow flex items-center gap-1.5 mb-1.5">
          <BookOpen size={11} /> Teams tier
        </div>
        <h1 className="font-display font-extrabold text-3xl lg:text-4xl tracking-tight">API Documentation</h1>
        <p className="text-[var(--ink-2)] mt-2 max-w-2xl leading-relaxed">
          Build automations on top of iSpeedToClose — sync leads from another CRM, score deals from a Zap,
          or push portfolio updates from your accounting system. Authentication uses Personal Access Tokens.
        </p>
      </header>

      <div className="grid lg:grid-cols-[1fr_320px] gap-6">
        <div className="space-y-6 min-w-0">
          {/* Quickstart */}
          <section className="card" data-testid="api-docs-quickstart">
            <div className="label-eyebrow flex items-center gap-1.5 mb-2"><Zap size={11} /> Quickstart</div>
            <h2 className="font-display font-extrabold text-xl mb-3">1. Authenticate every request</h2>
            <p className="text-sm text-[var(--ink-2)] mb-4">
              Every API call needs an <code className="font-mono text-xs bg-slate-100 px-1 rounded">Authorization</code> header.
              Smoke-test your token with <code className="font-mono text-xs bg-slate-100 px-1 rounded">GET /api/auth/me</code> first.
            </p>

            <SnippetTabs
              tabs={[
                {
                  label: "cURL",
                  language: "bash",
                  code: `curl -sS "${meEndpoint}" \\\n  -H "Authorization: Bearer ${exampleKey}"`,
                },
                {
                  label: "Python",
                  language: "python",
                  code: `import requests\n\nresp = requests.get(\n    "${meEndpoint}",\n    headers={"Authorization": "Bearer ${exampleKey}"},\n    timeout=10,\n)\nresp.raise_for_status()\nprint(resp.json())`,
                },
                {
                  label: "Node",
                  language: "javascript",
                  code: `const res = await fetch("${meEndpoint}", {\n  headers: { Authorization: "Bearer ${exampleKey}" },\n});\nif (!res.ok) throw new Error(\`HTTP \${res.status}\`);\nconsole.log(await res.json());`,
                },
              ]}
            />
          </section>

          {/* Rate limits */}
          <section className="card" data-testid="api-docs-rate-limits">
            <div className="label-eyebrow flex items-center gap-1.5 mb-2"><Activity size={11} /> Rate limits</div>
            <h2 className="font-display font-extrabold text-xl mb-3">2. Stay under the limits</h2>
            <div className="grid sm:grid-cols-2 gap-4">
              <LimitTile
                label="Per minute"
                value={catalog.auth.rate_limits.per_minute}
                hint="Sliding-window. Bursts above this return 429."
              />
              <LimitTile
                label="Per day"
                value={catalog.auth.rate_limits.per_day}
                hint="Resets continuously over 24h. Email us for higher limits."
              />
            </div>
            <p className="text-xs text-[var(--ink-3)] mt-3">
              On 429 responses, the error message includes the seconds to wait before retrying.
              Build exponential backoff into batch jobs.
            </p>
          </section>

          {/* Endpoint reference */}
          <section data-testid="api-docs-endpoints">
            <div className="label-eyebrow flex items-center gap-1.5 mb-2"><Code2 size={11} /> Reference</div>
            <h2 className="font-display font-extrabold text-xl mb-4">3. Endpoint reference</h2>
            <div className="space-y-4">
              {catalog.groups.map((g) => (
                <EndpointGroup key={g.id} group={g} baseUrl={baseUrl} />
              ))}
            </div>
          </section>

          {/* Outbound webhooks */}
          <section data-testid="api-docs-webhooks-section">
            <div className="label-eyebrow flex items-center gap-1.5 mb-2"><Terminal size={11} /> Real-time push</div>
            <h2 className="font-display font-extrabold text-xl mb-1">4. Subscribe to events with webhooks</h2>
            <p className="text-sm text-[var(--ink-2)] mb-4 leading-relaxed">
              Instead of polling, register a receiver URL and we&apos;ll POST you the moment a lead is
              created, an AI verdict turns GO, or a buyer fills out a capture form. Each request is
              signed with HMAC-SHA256 — verify it on your end so spoofed requests can&apos;t leak in.
            </p>
            <WebhooksCard />
            <div className="mt-4">
              <div className="text-[10px] uppercase tracking-wide text-[var(--ink-3)] font-bold mb-1">Verify signatures (Python)</div>
              <SnippetTabs
                tabs={[
                  {
                    label: "Python",
                    language: "python",
                    code: `import hmac, hashlib\n\ndef verify(raw_body: bytes, header: str, secret: str) -> bool:\n    # header is "sha256=<hex digest>"\n    expected = "sha256=" + hmac.new(secret.encode(), raw_body, hashlib.sha256).hexdigest()\n    return hmac.compare_digest(expected, header)\n\n# In your Flask/FastAPI handler:\n#   if not verify(request.body, request.headers["X-IST-Signature"], YOUR_SECRET):\n#       return 401`,
                  },
                  {
                    label: "Node",
                    language: "javascript",
                    code: `import crypto from "node:crypto";\n\nfunction verify(rawBody, header, secret) {\n  const expected = "sha256=" + crypto\n    .createHmac("sha256", secret)\n    .update(rawBody)\n    .digest("hex");\n  return crypto.timingSafeEqual(Buffer.from(expected), Buffer.from(header));\n}`,
                  },
                ]}
              />
            </div>
          </section>
        </div>

        {/* Right rail: usage + key management */}
        <aside className="space-y-6 min-w-0">
          <section className="card" data-testid="api-docs-usage">
            <div className="label-eyebrow flex items-center gap-1.5 mb-2"><Activity size={11} /> Usage</div>
            <KeyPicker keys={keys} selected={selectedKeyId} onPick={setSelectedKeyId} />
            {usageLoading ? (
              <div className="py-6 text-center text-sm text-[var(--ink-3)]">
                <Loader2 className="animate-spin inline" size={14} /> Loading…
              </div>
            ) : usage ? (
              <UsagePanel usage={usage} />
            ) : keys.length === 0 ? (
              <div className="text-sm text-[var(--ink-3)] py-4">
                Create a key below to see usage analytics here.
              </div>
            ) : (
              <div className="text-sm text-[var(--ink-3)] py-4">No data yet for this key.</div>
            )}
          </section>

          <ApiKeysCard />

          <div className="card">
            <div className="label-eyebrow mb-2">Need help?</div>
            <p className="text-sm text-[var(--ink-2)] leading-relaxed">
              Email <a className="text-[var(--primary)] underline" href="mailto:team@ispeedtoclose.com">team@ispeedtoclose.com</a> —
              we triage Team-tier integration tickets within one business day.
            </p>
          </div>
        </aside>
      </div>
    </div>
  );
}

function GatedView() {
  return (
    <div className="p-6 max-w-3xl mx-auto" data-testid="api-docs-gated">
      <div className="card text-center py-10">
        <div className="w-12 h-12 mx-auto rounded-full bg-amber-100 flex items-center justify-center mb-4">
          <Lock className="text-amber-700" size={20} />
        </div>
        <h1 className="font-display font-extrabold text-2xl mb-2">API access is a Teams-tier perk</h1>
        <p className="text-[var(--ink-2)] max-w-md mx-auto mb-5 leading-relaxed">
          Upgrade to Teams to issue Personal Access Tokens, hit our REST API,
          and wire iSpeedToClose into your existing CRM/automation stack.
        </p>
        <Link to="/app/billing" className="btn btn-primary inline-flex" data-testid="api-docs-upgrade-cta">
          See plans <ArrowUpRight size={14} />
        </Link>
      </div>
    </div>
  );
}

function LimitTile({ label, value, hint }) {
  return (
    <div className="rounded-lg border border-slate-200 p-3">
      <div className="text-xs text-[var(--ink-3)] uppercase tracking-wide font-semibold">{label}</div>
      <div className="font-display font-extrabold text-2xl mt-1">{value.toLocaleString()}</div>
      <div className="text-xs text-[var(--ink-3)] mt-1 leading-snug">{hint}</div>
    </div>
  );
}

function MethodTag({ method }) {
  const colors = {
    GET: "bg-emerald-100 text-emerald-800",
    POST: "bg-sky-100 text-sky-800",
    PATCH: "bg-amber-100 text-amber-800",
    PUT: "bg-amber-100 text-amber-800",
    DELETE: "bg-rose-100 text-rose-800",
  };
  return (
    <span className={`text-[10px] font-mono font-bold px-1.5 py-0.5 rounded ${colors[method] || "bg-slate-100 text-slate-700"}`}>
      {method}
    </span>
  );
}

function EndpointGroup({ group, baseUrl }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="card !p-0 overflow-hidden" data-testid={`api-group-${group.id}`}>
      <button
        type="button"
        className="w-full text-left p-4 hover:bg-slate-50 transition-colors flex items-center justify-between gap-3"
        onClick={() => setOpen((v) => !v)}
        data-testid={`api-group-toggle-${group.id}`}
      >
        <div>
          <div className="font-display font-extrabold text-base">{group.label}</div>
          <div className="text-sm text-[var(--ink-2)] mt-0.5">{group.blurb}</div>
        </div>
        <span className="text-xs text-[var(--ink-3)] font-mono whitespace-nowrap">
          {group.endpoints.length} endpoint{group.endpoints.length !== 1 ? "s" : ""}
        </span>
      </button>
      {open && (
        <div className="border-t border-slate-100 divide-y divide-slate-100">
          {group.endpoints.map((ep, idx) => (
            <Endpoint key={idx} ep={ep} baseUrl={baseUrl} groupId={group.id} idx={idx} />
          ))}
        </div>
      )}
    </div>
  );
}

function Endpoint({ ep, baseUrl, groupId, idx }) {
  const exampleKey = "pat_xxx";
  const fullPath = `${baseUrl}${ep.path}`;
  const curl = ep.method === "GET" || ep.method === "DELETE"
    ? `curl -sS -X ${ep.method} "${fullPath}${ep.example_body && ep.example_body.startsWith("?") ? ep.example_body : ""}" \\\n  -H "Authorization: Bearer ${exampleKey}"`
    : `curl -sS -X ${ep.method} "${fullPath}" \\\n  -H "Authorization: Bearer ${exampleKey}" \\\n  -H "Content-Type: application/json" \\\n  -d '${ep.example_body || "{}"}'`;
  return (
    <div className="p-4" data-testid={`api-endpoint-${groupId}-${idx}`}>
      <div className="flex items-center gap-2 mb-1.5 flex-wrap">
        <MethodTag method={ep.method} />
        <code className="font-mono text-sm text-[var(--ink-1)] break-all">{ep.path}</code>
      </div>
      <div className="text-sm text-[var(--ink-2)] mb-2">{ep.summary}</div>
      <CopyBlock code={curl} language="bash" />
      {ep.example_response && (
        <div className="mt-2">
          <div className="text-[10px] uppercase tracking-wide text-[var(--ink-3)] font-bold mb-1">Example response</div>
          <CopyBlock code={ep.example_response} language="json" muted />
        </div>
      )}
    </div>
  );
}

function SnippetTabs({ tabs }) {
  const [active, setActive] = useState(0);
  const t = tabs[active];
  return (
    <div data-testid="api-snippet-tabs">
      <div className="flex gap-1 mb-2 border-b border-slate-200">
        {tabs.map((tab, i) => (
          <button
            type="button"
            key={tab.label}
            onClick={() => setActive(i)}
            className={`text-xs font-semibold px-3 py-1.5 border-b-2 transition-colors ${
              i === active ? "border-[var(--primary)] text-[var(--ink-1)]" : "border-transparent text-[var(--ink-3)] hover:text-[var(--ink-1)]"
            }`}
            data-testid={`api-snippet-tab-${tab.label.toLowerCase()}`}
          >
            {tab.label}
          </button>
        ))}
      </div>
      <CopyBlock code={t.code} language={t.language} />
    </div>
  );
}

function CopyBlock({ code, language, muted }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch { /* noop */ }
  };
  return (
    <div className="relative group">
      <pre
        className={`font-mono text-xs leading-relaxed p-3 rounded-lg overflow-x-auto ${
          muted ? "bg-slate-50 text-slate-700 border border-slate-200" : "bg-slate-900 text-slate-100"
        }`}
      >
        <code data-language={language}>{code}</code>
      </pre>
      <button
        type="button"
        onClick={copy}
        className="absolute top-2 right-2 p-1.5 rounded bg-white/10 hover:bg-white/20 text-white/80 opacity-0 group-hover:opacity-100 transition-opacity"
        data-testid="copy-snippet"
        aria-label="Copy snippet"
      >
        {copied ? <Check size={12} /> : <Copy size={12} />}
      </button>
    </div>
  );
}

function KeyPicker({ keys, selected, onPick }) {
  if (!keys || keys.length === 0) return null;
  return (
    <div className="mb-3">
      <label className="text-[10px] uppercase tracking-wide text-[var(--ink-3)] font-bold block mb-1">Key</label>
      <select
        value={selected || ""}
        onChange={(e) => onPick(e.target.value)}
        className="w-full px-2 py-1.5 rounded-lg border border-slate-200 text-sm focus:outline-none focus:border-[var(--primary)]"
        data-testid="usage-key-picker"
      >
        {keys.map((k) => (
          <option key={k.id} value={k.id}>
            {k.name}{k.revoked_at ? " (revoked)" : ""}
          </option>
        ))}
      </select>
    </div>
  );
}

function UsagePanel({ usage }) {
  const totals = usage.totals || {};
  const max = Math.max(1, ...(usage.sparkline || []).map((d) => d.count));
  const rate = usage.rate_limit || {};
  return (
    <div data-testid="usage-panel">
      <div className="grid grid-cols-3 gap-2 mb-3">
        <SmallStat label="24h" value={totals.last_24h ?? 0} />
        <SmallStat label="7d" value={totals.last_7d ?? 0} />
        <SmallStat label="30d" value={totals.last_30d ?? 0} />
      </div>

      <div className="text-[10px] uppercase tracking-wide text-[var(--ink-3)] font-bold mb-1">Last 14 days</div>
      <div className="flex items-end gap-0.5 h-12 mb-3" aria-label="14 day usage">
        {(usage.sparkline || []).map((d) => (
          <div
            key={d.date}
            className="flex-1 bg-[var(--primary)] rounded-sm transition-colors"
            style={{ height: `${(d.count / max) * 100}%`, minHeight: d.count > 0 ? "3px" : "1px", opacity: d.count > 0 ? 0.9 : 0.15 }}
            title={`${d.date}: ${d.count}`}
          />
        ))}
      </div>

      <div className="text-[10px] uppercase tracking-wide text-[var(--ink-3)] font-bold mb-1">Live rate window</div>
      <RateMeter
        label="This minute"
        used={rate.per_minute_used ?? 0}
        limit={rate.per_minute_limit ?? 0}
      />
      <RateMeter
        label="Today"
        used={rate.per_day_used ?? 0}
        limit={rate.per_day_limit ?? 0}
      />

      {usage.top_paths && usage.top_paths.length > 0 && (
        <>
          <div className="text-[10px] uppercase tracking-wide text-[var(--ink-3)] font-bold mb-1 mt-3">Top endpoints (7d)</div>
          <ul className="space-y-1 mb-3" data-testid="usage-top-paths">
            {usage.top_paths.slice(0, 5).map((p, i) => (
              <li key={i} className="flex items-center justify-between gap-2 text-xs">
                <span className="font-mono truncate flex items-center gap-1 min-w-0">
                  <MethodTag method={p.method} />
                  <span className="truncate">{p.path}</span>
                </span>
                <span className="font-semibold text-[var(--ink-2)] whitespace-nowrap">{p.count}</span>
              </li>
            ))}
          </ul>
        </>
      )}

      {usage.recent && usage.recent.length > 0 && (
        <>
          <div className="text-[10px] uppercase tracking-wide text-[var(--ink-3)] font-bold mb-1">Recent calls</div>
          <ul className="space-y-1 max-h-40 overflow-y-auto" data-testid="usage-recent">
            {usage.recent.slice(0, 10).map((r, i) => (
              <li key={i} className="flex items-center justify-between gap-2 text-[11px]">
                <span className="font-mono truncate flex items-center gap-1 min-w-0">
                  <MethodTag method={r.method} />
                  <span className="truncate">{r.path}</span>
                </span>
                <span className="text-[var(--ink-3)] whitespace-nowrap">
                  {r.ts ? new Date(r.ts).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : ""}
                </span>
              </li>
            ))}
          </ul>
        </>
      )}
    </div>
  );
}

function SmallStat({ label, value }) {
  return (
    <div className="rounded-lg bg-slate-50 border border-slate-200 px-2 py-1.5">
      <div className="text-[10px] text-[var(--ink-3)] uppercase font-bold">{label}</div>
      <div className="font-display font-extrabold text-lg leading-tight">{value.toLocaleString()}</div>
    </div>
  );
}

function RateMeter({ label, used, limit }) {
  const pct = limit > 0 ? Math.min(100, (used / limit) * 100) : 0;
  const color = pct > 85 ? "bg-rose-500" : pct > 60 ? "bg-amber-500" : "bg-emerald-500";
  return (
    <div className="mb-2">
      <div className="flex items-center justify-between text-[11px] mb-0.5">
        <span className="text-[var(--ink-2)]">{label}</span>
        <span className="font-mono text-[var(--ink-3)]">{used} / {limit}</span>
      </div>
      <div className="h-1.5 rounded-full bg-slate-100 overflow-hidden">
        <div className={`h-full ${color} transition-all`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}
